import type { AccountingPeriodView } from '@chukua/common';
import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { AccountingPeriodsTable } from '../components/AccountingPeriodsTable';
import { buildPeriodColumns } from '../components/build-period-columns';
import { PeriodRuleCard } from '../components/PeriodRuleCard';
import { AddPeriodButton } from '../components/AddPeriodButton';
import { FirstPeriodModal } from '../components/FirstPeriodModal';
import { UnlockPeriodModal } from '../components/UnlockPeriodModal';
import { useAccountingPeriods } from '../hooks/use-accounting-periods';
import { useAddPeriod } from '../hooks/use-add-period';
import { usePeriodPermissions } from '../hooks/use-period-permissions';
import { usePeriodRule } from '../hooks/use-period-rule';
import type { PeriodActionKind } from '../policies/period-actions.policy';

export function AccountingPeriodsPage() {
  const periods = useAccountingPeriods();
  const permissions = usePeriodPermissions();
  const rule = usePeriodRule(permissions.canManage);
  const [unlocking, setUnlocking] = useState<AccountingPeriodView | null>(null);
  const addPeriod = useAddPeriod({ hasNoPeriods: periods.hasNoPeriods, reload: periods.reload });

  if (periods.state.status === 'forbidden') return <Navigate to="/" replace />;

  function act(period: AccountingPeriodView, action: PeriodActionKind) {
    if (action === 'close') void periods.close(period);
    if (action === 'lock') void periods.lock(period);
    if (action === 'unlock') {
      periods.resetUnlockError();
      setUnlocking(period);
    }
  }

  const columns = buildPeriodColumns({ list: periods.list, permissions, busyId: periods.busyId, onAct: act });

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('accounting-periods')}
        title="Accounting periods"
        description="Each month's books. A locked period refuses new entries; late items go to the next open period, marked as adjustments."
        actions={permissions.canManage && <AddPeriodButton isFirst={periods.hasNoPeriods} onClick={addPeriod.start} />}
      />
      {permissions.canManage && rule.startDay !== null && (
        <PeriodRuleCard key={rule.startDay} startDay={rule.startDay} saving={rule.saving} onSave={rule.save} />
      )}
      {periods.state.status === 'error' && (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load periods"
          description="Something went wrong loading the accounting periods. Try reloading the page."
        />
      )}
      {periods.state.status === 'loading' && <TableSkeleton label="Loading accounting periods" withSearch />}
      {periods.state.status === 'loaded' && (
        <AccountingPeriodsTable page={periods.state.page} columns={columns} list={periods.list} isRefreshing={periods.isRefreshing} />
      )}
      <FirstPeriodModal
        open={addPeriod.isDialogOpen}
        startDay={rule.startDay}
        error={addPeriod.error}
        submitting={false}
        onCreate={addPeriod.create}
        onClose={addPeriod.closeDialog}
      />
      <UnlockPeriodModal
        period={unlocking}
        error={periods.unlockError}
        submitting={periods.busyId !== null}
        onUnlock={periods.unlock}
        onClose={() => setUnlocking(null)}
      />
    </>
  );
}
